/**
 * VIP权限系统React Hooks
 * 提供权限检查、限制检查等功能
 */

import { useState, useEffect, useCallback } from 'react';
import { vipApiService } from '../services/vipApi.js';
import type {
  PermissionInfoResponse,
  VIPPermissions,
  VIPLimits,
  UserUsage,
  VIPGiftRule,
} from '../types/vip.js';
import { VIPLevel } from '../types/vip.js';

/**
 * 使用VIP权限信息的Hook
 */
export function useVIPPermissions() {
  const [permissions, setPermissions] = useState<PermissionInfoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // 获取权限信息
  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await vipApiService.getPermissions();
      setPermissions(data);

      // 缓存到localStorage
      localStorage.setItem('vipPermissions', JSON.stringify(data));
    } catch (err) {
      setError(err as Error);
      console.error('获取VIP权限失败:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 刷新权限信息
  const refreshPermissions = useCallback(async () => {
    try {
      setError(null);
      const data = await vipApiService.refreshPermissions();
      setPermissions(data);

      // 更新localStorage
      localStorage.setItem('vipPermissions', JSON.stringify(data));

      return data;
    } catch (err) {
      setError(err as Error);
      console.error('刷新VIP权限失败:', err);
      throw err;
    }
  }, []);

  // 组件挂载时从localStorage加载权限
  useEffect(() => {
    const cached = localStorage.getItem('vipPermissions');
    if (cached) {
      try {
        setPermissions(JSON.parse(cached));
        setLoading(false);
      } catch (err) {
        console.error('解析缓存的权限信息失败:', err);
      }
    }

    // 然后从服务器获取最新权限
    fetchPermissions();
  }, [fetchPermissions]);

  return {
    permissions,
    loading,
    error,
    refetch: fetchPermissions,
    refresh: refreshPermissions,
  };
}

/**
 * 使用VIP权限检查的Hook
 */
export function useVIPPermission() {
  const { permissions } = useVIPPermissions();

  /**
   * 检查是否有某个功能权限
   */
  const hasPermission = useCallback((feature: keyof VIPPermissions): boolean => {
    if (!permissions) return false;
    return permissions.permissions[feature] === true;
  }, [permissions]);

  /**
   * 检查MCP工具权限
   */
  const hasMCPToolPermission = useCallback((toolLevel: 'basic' | 'extended' | 'all'): boolean => {
    if (!permissions) return false;
    const userLevel = permissions.permissions.mcp_tools;

    if (userLevel === 'all') return true;
    if (userLevel === 'extended') return toolLevel === 'basic' || toolLevel === 'extended';
    return toolLevel === 'basic';
  }, [permissions]);

  /**
   * 检查是否达到限制
   */
  const checkLimit = useCallback((feature: 'role_slots' | 'memory_count' | 'kb_file_count'): boolean => {
    if (!permissions) return false;
    const limit = permissions.limits[feature];
    const current = permissions.usage[feature as keyof UserUsage] as number;

    // -1 表示无限制
    if (limit === -1) return true;

    return current < limit;
  }, [permissions]);

  /**
   * 获取剩余数量
   */
  const getRemaining = useCallback((feature: 'role_slots' | 'memory_count' | 'kb_file_count'): number => {
    if (!permissions) return 0;
    const limit = permissions.limits[feature];
    const current = permissions.usage[feature as keyof UserUsage] as number;

    // -1 表示无限制
    if (limit === -1) return -1;

    return Math.max(0, limit - current);
  }, [permissions]);

  /**
   * 获取VIP等级信息
   */
  const getVIPLevel = useCallback(() => {
    if (!permissions) return { level: VIPLevel.FREE, code: 'free', name: '免费用户' };
    return {
      level: permissions.vipLevel,
      code: permissions.vipLevelCode,
      name: permissions.vipLevelName,
    };
  }, [permissions]);

  /**
   * 检查是否是VIP用户
   */
  const isVIP = useCallback(() => {
    if (!permissions) return false;
    return permissions.vipLevel >= VIPLevel.VIP;
  }, [permissions]);

  /**
   * 检查是否是VVIP用户
   */
  const isVVIP = useCallback(() => {
    if (!permissions) return false;
    return permissions.vipLevel >= VIPLevel.VVIP;
  }, [permissions]);

  /**
   * 检查VIP是否已过期
   */
  const isExpired = useCallback(() => {
    if (!permissions) return false;
    return permissions.isExpired;
  }, [permissions]);

  /**
   * 获取VIP剩余天数
   */
  const getDaysRemaining = useCallback(() => {
    if (!permissions) return null;
    return permissions.daysRemaining;
  }, [permissions]);

  return {
    permissions,
    hasPermission,
    hasMCPToolPermission,
    checkLimit,
    getRemaining,
    getVIPLevel,
    isVIP: isVIP(),
    isVVIP: isVVIP(),
    isExpired: isExpired(),
    daysRemaining: getDaysRemaining(),
  };
}

/**
 * 使用VIP赠送规则的Hook
 */
export function useVIPGiftRules() {
  const [rules, setRules] = useState<VIPGiftRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchRules = async () => {
      try {
        setLoading(true);
        setError(null);
        const { rules: data } = await vipApiService.getGiftRules();
        setRules(data);
      } catch (err) {
        setError(err as Error);
        console.error('获取VIP赠送规则失败:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
  }, []);

  return { rules, loading, error };
}

/**
 * 使用VIP激活历史的Hook
 */
export function useVIPActivationHistory(limit = 10) {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const { history: data } = await vipApiService.getActivationHistory(limit);
        setHistory(data);
      } catch (err) {
        setError(err as Error);
        console.error('获取VIP激活历史失败:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [limit]);

  return { history, loading, error };
}

/**
 * 权限检查Hook(用于受保护的功能)
 */
export function useRequirePermission(feature: keyof VIPPermissions) {
  const { permissions, loading } = useVIPPermissions();

  if (loading) {
    return { allowed: false, loading: true };
  }

  if (!permissions) {
    return { allowed: false, loading: false, reason: '权限信息未加载' };
  }

  const allowed = permissions.permissions[feature] === true;
  const reason = allowed ? undefined : `该功能需要VIP会员权限`;

  return { allowed, loading: false, reason, permissions };
}

/**
 * 限制检查Hook(用于有数量限制的功能)
 */
export function useRequireLimit(feature: 'role_slots' | 'memory_count' | 'kb_file_count') {
  const { permissions, loading } = useVIPPermissions();

  if (loading) {
    return { allowed: false, loading: true, current: 0, limit: 0, remaining: 0 };
  }

  if (!permissions) {
    return { allowed: false, loading: false, current: 0, limit: 0, remaining: 0 };
  }

  const limit = permissions.limits[feature];
  const current = permissions.usage[feature as keyof UserUsage] as number;
  const remaining = limit === -1 ? -1 : Math.max(0, limit - current);
  const allowed = limit === -1 || current < limit;

  const message = allowed
    ? undefined
    : `已达到${permissions.limits[`${feature}_display` as keyof VIPLimits] || feature}上限`;

  return { allowed, loading: false, current, limit, remaining, message, permissions };
}
